import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useClientAccess } from '../hooks/useClientAccess';
import { useI18n } from '../contexts/I18nContext';
import { auditService } from '../services/auditService';
import { AuditListParams, AuditLogEntry } from '../types/audit';
import AuditLogTable from '../components/audit/AuditLogTable';
import AuditDashboard from '../components/audit/AuditDashboard';
import { isAbortError } from '../utils/abortError';
import { useAuditUrlState } from '../hooks/useAuditUrlState';

const AUTO_REFRESH_INTERVAL_MS = 30_000;

// Build a CSV filename that reflects the active filter context — e.g.
// `audit-log-failed-actor-alice-2026-05-05.csv` — so users can tell
// exports apart later. Keeps file-system-safe characters only.
function buildExportFilename(filters: AuditListParams): string {
  const parts = ['audit-log'];
  if (filters.status) parts.push(filters.status);
  if (filters.resource_type) parts.push(filters.resource_type);
  if (filters.actor_username) parts.push('actor-' + filters.actor_username);
  if (filters.action) parts.push(filters.action);
  parts.push(new Date().toISOString().slice(0, 10));
  return (
    parts
      .map((p) => p.replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/^-+|-+$/g, ''))
      .filter(Boolean)
      .join('-') + '.csv'
  );
}

export const AuditLogPage: React.FC = () => {
  const { canViewAuditLog } = useClientAccess();
  const { t, formatTime } = useI18n();

  const {
    state: { tab: activeTab, filters, page, pageSize },
    setTab,
    setFilters,
    setPage,
    setPageSize,
    patch,
  } = useAuditUrlState();

  const [items, setItems] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [autoRefresh, setAutoRefresh] = useState<boolean>(false);

  // Track the in-flight load so cleanup can abort it. Refresh button
  // calls load() outside an effect, so it gets its own controller too.
  const loadAbortRef = useRef<AbortController | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    if (!canViewAuditLog) return;
    setLoading(true);
    setError(null);
    try {
      const response = await auditService.listAuditLog(
        { ...filters, page, page_size: pageSize },
        { signal },
      );
      setItems(response.items || []);
      setTotal(response.total || 0);
      setLastUpdated(new Date());
    } catch (err) {
      if (isAbortError(err)) return;
      console.error('[AUDIT LOG PAGE] Failed to fetch audit entries:', err);
      setError(err instanceof Error ? err.message : 'Failed to load audit log');
      setItems([]);
      setTotal(0);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [canViewAuditLog, filters, page, pageSize]);

  useEffect(() => {
    const ac = new AbortController();
    loadAbortRef.current = ac;
    load(ac.signal);
    return () => ac.abort();
  }, [load]);

  // Auto-refresh: poll the list view every 30 s while the toggle is on
  // and the tab/document is visible. We pause when the document is
  // hidden so background tabs don't hammer the API.
  useEffect(() => {
    if (!autoRefresh || activeTab !== 'list') return;
    let intervalId: number | undefined;
    const start = () => {
      stop();
      intervalId = window.setInterval(() => {
        if (document.visibilityState === 'visible') {
          loadAbortRef.current?.abort();
          const ac = new AbortController();
          loadAbortRef.current = ac;
          load(ac.signal);
        }
      }, AUTO_REFRESH_INTERVAL_MS);
    };
    const stop = () => {
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
        intervalId = undefined;
      }
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') start();
      else stop();
    };
    start();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [autoRefresh, activeTab, load]);

  const handleRefresh = useCallback(() => {
    loadAbortRef.current?.abort();
    const ac = new AbortController();
    loadAbortRef.current = ac;
    load(ac.signal);
  }, [load]);

  // Drill-down from the dashboard: merge the supplied filters with the
  // current ones, jump to page 1, and switch to the Events tab so the
  // user lands on the matching rows.
  const handleDrillDown = useCallback(
    (next: Partial<AuditListParams>) => {
      patch({
        filters: { ...filters, ...next },
        page: 1,
        tab: 'list',
      });
    },
    [filters, patch],
  );

  const handleExportCsv = useCallback(async () => {
    if (!canViewAuditLog) return;
    setLoading(true);
    setError(null);
    try {
      const EXPORT_PAGE_SIZE = 500;
      const MAX_PAGES = 20; // Safety cap — 10k rows max per export
      const MAX_CONCURRENCY = 5; // Don't hammer the API with all pages at once

      // Page 1 reveals the total, so the remaining pages are independent and
      // can be fetched in parallel (bounded) instead of one round-trip at a time.
      const first = await auditService.listAuditLog({
        ...filters,
        page: 1,
        page_size: EXPORT_PAGE_SIZE,
      });
      const rows: AuditLogEntry[] = [...(first.items || [])];
      const totalPages = Math.min(
        MAX_PAGES,
        Math.ceil((first.total || rows.length) / EXPORT_PAGE_SIZE),
      );

      const pageNumbers: number[] = [];
      for (let p = 2; p <= totalPages; p++) pageNumbers.push(p);

      // Fetch in ascending batches so concurrency stays bounded and CSV row
      // order matches page order.
      for (let i = 0; i < pageNumbers.length; i += MAX_CONCURRENCY) {
        const batch = pageNumbers.slice(i, i + MAX_CONCURRENCY);
        const responses = await Promise.all(
          batch.map((p) =>
            auditService.listAuditLog({
              ...filters,
              page: p,
              page_size: EXPORT_PAGE_SIZE,
            }),
          ),
        );
        for (const response of responses) rows.push(...(response.items || []));
      }
      const escape = (v: unknown): string => {
        if (v === null || v === undefined) return '';
        const s = typeof v === 'string' ? v : JSON.stringify(v);
        return '"' + s.replace(/"/g, '""').replace(/\r?\n/g, ' ') + '"';
      };
      const header = [
        'timestamp',
        'actor_username',
        'actor_ip',
        'actor_roles',
        'action',
        'resource_type',
        'resource_id',
        'http_method',
        'http_path',
        'status',
        'old_value',
        'new_value',
        'error_message',
      ];
      const lines = [header.join(',')];
      for (const r of rows) {
        lines.push(
          [
            escape(r.timestamp),
            escape(r.actor_username),
            escape(r.actor_ip),
            escape(r.actor_roles ? r.actor_roles.join('|') : ''),
            escape(r.action),
            escape(r.resource_type),
            escape(r.resource_id),
            escape(r.http_method),
            escape(r.http_path),
            escape(r.status),
            escape(r.old_value),
            escape(r.new_value),
            escape(r.error_message),
          ].join(','),
        );
      }
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = buildExportFilename(filters);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[AUDIT LOG PAGE] CSV export failed:', err);
      setError(err instanceof Error ? err.message : 'CSV export failed');
    } finally {
      setLoading(false);
    }
  }, [canViewAuditLog, filters]);

  if (!canViewAuditLog) {
    return (
      <div className="page" data-testid="audit-access-denied">
        <div className="page-content">
          <div className="page-header">
            <div className="page-title">
              <h1>📜 {t('audit.pageTitle')}</h1>
              <p>{t('audit.pageSubtitle')}</p>
            </div>
          </div>
          <div className="audit-log-container">
            <div className="audit-empty">{t('audit.accessDenied')}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page audit-log-page">
      <div className="page-content">
        <div className="page-header">
          <div className="page-title">
            <h1>📜 {t('audit.pageTitle')}</h1>
            <p>{t('audit.pageSubtitle')}</p>
          </div>
          <div className="page-actions">
            <div className="last-updated">
              {t('audit.lastUpdated')}: {formatTime(lastUpdated)}
            </div>
            <label className="audit-auto-refresh" data-testid="audit-auto-refresh">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                aria-label={t('audit.autoRefresh')}
              />
              {t('audit.autoRefresh')}
            </label>
            <button
              className="refresh-button"
              onClick={handleRefresh}
              disabled={loading}
              data-testid="audit-refresh"
            >
              🔄 {t('audit.refresh')}
            </button>
          </div>
        </div>

        {error && <div className="audit-error-banner">{error}</div>}

        <div className="audit-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'dashboard'}
            className={`audit-tab${activeTab === 'dashboard' ? ' active' : ''}`}
            onClick={() => setTab('dashboard')}
            data-testid="audit-tab-dashboard"
          >
            {t('audit.dashboard.tabDashboard')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'list'}
            className={`audit-tab${activeTab === 'list' ? ' active' : ''}`}
            onClick={() => setTab('list')}
            data-testid="audit-tab-list"
          >
            {t('audit.dashboard.tabList')}
          </button>
        </div>

        {activeTab === 'dashboard' ? (
          <AuditDashboard
            refreshKey={lastUpdated.getTime()}
            onDrillDown={handleDrillDown}
          />
        ) : (
          <AuditLogTable
            items={items}
            loading={loading}
            total={total}
            page={page}
            pageSize={pageSize}
            filters={filters}
            onFilterChange={setFilters}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            onExportCsv={handleExportCsv}
          />
        )}
      </div>
    </div>
  );
};

export default AuditLogPage;
